// Google Analytics 4 helper utilities — Basic Consent Mode.
// The GA4 library is NOT loaded until the visitor grants statistics consent.
// No GA4 requests or cookieless pings are sent before consent.

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

const GA4_ID = 'G-L7N8HS22XF';
const CONSENT_KEY = 'easy_cookie_consent_v1';

export type ConsentChoice = 'accepted' | 'rejected' | 'custom';

export interface ConsentSettings {
  analytics_storage: 'granted' | 'denied';
  ad_storage: 'granted' | 'denied';
  ad_user_data: 'granted' | 'denied';
  ad_personalization: 'granted' | 'denied';
  functionality_storage: 'granted' | 'denied';
}

const ALL_GRANTED: ConsentSettings = {
  analytics_storage: 'granted',
  ad_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
  functionality_storage: 'granted',
};

const ALL_DENIED: ConsentSettings = {
  analytics_storage: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  functionality_storage: 'denied',
};

let ga4Loaded = false;

function gtag(...args: unknown[]): void {
  if (typeof window.gtag === 'function') {
    window.gtag(...args);
  }
}

/**
 * Inject the GA4 gtag library and configure the measurement ID.
 * Called only once, after the visitor grants statistics consent.
 */
function loadGa4(): void {
  if (ga4Loaded) return;
  ga4Loaded = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(script);

  gtag('js', new Date());
  gtag('config', GA4_ID, {
    'cookie_flags': 'SameSite=None;Secure',
  });
}

export function getStoredConsent(): { choice: ConsentChoice; settings: ConsentSettings } | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { choice: ConsentChoice; settings: ConsentSettings };
    return parsed;
  } catch {
    return null;
  }
}

export function hasConsent(): boolean {
  const stored = getStoredConsent();
  return !!stored && stored.settings.analytics_storage === 'granted';
}

export function applyConsent(settings: ConsentSettings): void {
  gtag('consent', 'update', settings);
}

export function saveConsent(choice: ConsentChoice, settings: ConsentSettings): void {
  const payload = JSON.stringify({ choice, settings });
  try {
    localStorage.setItem(CONSENT_KEY, payload);
  } catch {
    // localStorage unavailable — consent not persisted, banner reappears next load
  }
  applyConsent(settings);
  if (settings.analytics_storage === 'granted') {
    loadGa4();
  }
}

export function acceptAll(): void {
  saveConsent('accepted', ALL_GRANTED);
}

export function rejectAll(): void {
  saveConsent('rejected', ALL_DENIED);
}

export function saveCustom(settings: ConsentSettings): void {
  saveConsent('custom', settings);
}

export function revokeConsent(): void {
  // User withdrew consent — revert to denied and clear stored choice.
  // GA4 remains loaded but Consent Mode blocks further tracking.
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch {
    // ignore
  }
  applyConsent(ALL_DENIED);
}

/**
 * On app startup, if the visitor previously granted statistics consent,
 * load GA4 and restore the consent state. Otherwise GA4 stays unloaded.
 */
export function initAnalytics(): void {
  const stored = getStoredConsent();
  if (stored) {
    applyConsent(stored.settings);
    if (stored.settings.analytics_storage === 'granted') {
      loadGa4();
    }
  }
}

// --- Event tracking ---

export interface EventParams {
  [key: string]: unknown;
}

/**
 * Send a GA4 event. Only fires when analytics consent has been granted
 * and the GA4 library has been loaded.
 * Never pass personally identifiable information through the params.
 */
export function trackEvent(eventName: string, params?: EventParams): void {
  if (!hasConsent() || !ga4Loaded) return;
  gtag('event', eventName, params);
}

export function trackWhatsAppClick(buttonLocation: string): void {
  trackEvent('whatsapp_click', {
    button_location: buttonLocation,
    page_location: typeof window !== 'undefined' ? window.location.pathname : undefined,
  });
}

export function trackPhoneClick(buttonLocation: string): void {
  trackEvent('phone_click', {
    button_location: buttonLocation,
    page_location: typeof window !== 'undefined' ? window.location.pathname : undefined,
  });
}

export function trackOfferClick(offerName: string, provider: string, category?: string): void {
  trackEvent('offer_click', {
    offer_name: offerName,
    provider: provider,
    category: category,
    page_location: typeof window !== 'undefined' ? window.location.pathname : undefined,
  });
}

export function trackPartnerClick(partnerName: string, buttonLocation: string): void {
  trackEvent('partner_click', {
    offer_name: partnerName,
    button_location: buttonLocation,
    page_location: typeof window !== 'undefined' ? window.location.pathname : undefined,
  });
}

export function trackContactClick(buttonLocation: string): void {
  trackEvent('contact_click', {
    button_location: buttonLocation,
    page_location: typeof window !== 'undefined' ? window.location.pathname : undefined,
  });
}

export { GA4_ID };
